/**
 * Dashboard.
 *
 * ⚠️  MOBILE-FIRST. `MobileDashboard` is the canonical view (most users).
 *     Desktop branch is a separate render path and a progressive enhancement.
 *     When changing user-facing copy, layout, or data:
 *       1. Update the mobile view first.
 *       2. Mirror the change to the desktop view only if the product needs it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardStats, leaderboard, listExercises, listWorkouts, streakInfo, streakHeat, tasksToday } from "../lib/api";
import { Activity, AlertTriangle, Award, ChevronRight, Coins, Dumbbell, GitCommitHorizontal, Play, Plus, ShieldCheck, Sparkles, Target, Trophy } from "lucide-react";
import StreakConsole from "../components/StreakConsole";
import GettingStartedCard from "../components/GettingStartedCard";
import DailyChallengeCard from "../components/DailyChallengeCard";
import StatInfoSheet, { STAT_INFO } from "../components/StatInfoSheet";
import { buildWeekDots, endOfDayLabel, nextTierText, tierBadge } from "../lib/streak-ui";
import { useAuth } from "../contexts/AuthContext";
import { readCache, writeCache } from "../lib/swr-cache";

// Fire-and-forget MediaPipe warmup so the user's first /workout visit feels instant.
// The model is large (~3MB) and we want it in browser cache before they tap "Train".
// If this fails we don't care — WorkoutSession's WARMING UP overlay still works as the
// safety net. Lazy-imported so it doesn't bloat Dashboard's initial JS bundle.
function prefetchPoseModel() {
    // Idle callback if available (Chrome/Firefox), else setTimeout — never block paint.
    const fire = () => {
        import("../lib/mediapipe-loader")
            .then((m) => m.getPoseLandmarker())
            .catch((err) => console.warn("[prefetch] pose model warmup failed:", err?.message || err));
    };
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(fire, { timeout: 4000 });
    } else {
        setTimeout(fire, 1500);
    }
}

export default function Dashboard() {
    const { user: authUser } = useAuth();
    // Stale-while-revalidate: paint the last known dashboard INSTANTLY from a
    // local cache, then refresh every section in the background. First-ever
    // visit (no cache) shows a skeleton instead of a spinner.
    const cacheKey = `dash_${authUser?.user_id || "anon"}`;
    const cached = useMemo(() => readCache(cacheKey), [cacheKey]);
    const [stats, setStats] = useState(cached?.stats || null);
    const [streak, setStreak] = useState(cached?.streak || null);
    const [tasks, setTasks] = useState(cached?.tasks || []);
    const [workouts, setWorkouts] = useState(cached?.workouts || []);
    const [heatData, setHeatData] = useState(cached?.heat || null); // { days, summary, uptime_pct }
    const [squatsId, setSquatsId] = useState(null); // first-workout deep link for new users
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [s, t, w, st] = await Promise.all([dashboardStats(), tasksToday(), listWorkouts(5), streakInfo()]);
                setStats(s.data);
                setTasks(t.data);
                setWorkouts(w.data || []);
                setStreak(st.data);
                writeCache(cacheKey, { ...readCache(cacheKey), stats: s.data, streak: st.data, tasks: t.data, workouts: w.data || [] });
            } catch (e) {
                console.error(e);
            }
            // Fire-and-forget the heat fetch — if it fails we fall back to the synthesized grid.
            try {
                const h = await streakHeat(30);
                setHeatData(h.data);
                writeCache(cacheKey, { ...readCache(cacheKey), heat: h.data });
            } catch (err) {
                console.warn("[dashboard] streak heat fetch failed; using fallback:", err?.message || err);
            }
        })();
        // Pre-warm the MediaPipe model in the background so the first /workout
        // navigation feels instant. Safe because the WorkoutSession overlay still
        // works as a fallback if this hasn't completed by then.
        prefetchPoseModel();
    }, [cacheKey]);

    // New-ish users (< 3 workouts) get Squats deep links in the day-zero card +
    // getting-started checklist — squats is the canonical first exercise.
    useEffect(() => {
        if (!stats || (stats.total_workouts || 0) >= 3) return;
        listExercises()
            .then((r) => {
                const squats = (r.data || []).find((e) => e.slug === "squats");
                if (squats) setSquatsId(squats.exercise_id);
            })
            .catch((err) => console.warn("[dashboard] exercises fetch failed:", err?.message || err));
    }, [stats]);

    // Prefer real per-day data from the backend; fall back to the synthesized
    // grid based on streak_count if the endpoint hasn't responded yet (or fails
    // for legacy users on older deployments).
    const heat = useMemo(() => {
        if (heatData?.days?.length) return heatData.days.map((d) => d.value);
        return makeHeat(stats?.user?.streak_count || 0);
    }, [heatData, stats?.user?.streak_count]);

    if (!stats) {
        return <DashboardSkeleton />;
    }

    const user = stats.user;
    const firstName = (user.name || "athlete").split(" ")[0].toUpperCase();
    // Real 30-day uptime % from backend; fall back to the streak-derived estimate
    // only when the new endpoint isn't reachable yet.
    const uptimePct = heatData?.uptime_pct != null
        ? heatData.uptime_pct.toFixed(1)
        : Math.min(99.9, 92 + (user.streak_count || 0) * 1.23).toFixed(1);

    return (
        <div data-testid="dashboard-page">
            <div className="desktop-only">
                <div className="page-head">
                    <div>
                        <div className="eyebrow">Daily standup</div>
                        <h1 className="page">MORNING, {firstName}.</h1>
                        <div className="page-sub">
                            {"// streak at "}
                            <b>{user.streak_count} days</b>
                            {". don't break the chain."}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={() => navigate("/exercises")} className="btn btn-ghost" data-testid="dashboard-quick-commit">
                            <Plus size={16} /> Quick set - 90s
                        </button>
                        <button onClick={() => navigate("/exercises")} className="btn btn-primary" data-testid="dashboard-quick-start">
                            <Play size={16} /> Start session
                        </button>
                    </div>
                </div>

                <div className="stat-row">
                    <StatTile kind="hero" icon={<Activity size={18} />} label="Streak" value={user.streak_count} unit="d" sub={`${uptimePct}% uptime this month`} testid="stat-streak" />
                    <StatTile icon={<Target size={18} />} label="Fit score" value={user.fit_score.toFixed(0)} unit="/100" sub="grows with every rep" testid="stat-fitscore" />
                    <StatTile kind="gold" icon={<Coins size={18} />} label="Coins" value={user.coins.toFixed(0)} sub="spend on streak protection" testid="stat-coins" />
                    <StatTile icon={<GitCommitHorizontal size={18} />} label="Workouts / wk" value={stats.week_workouts} sub="completed this week" testid="stat-week" />
                </div>

                <StreakConsole heat={heat} uptimePct={uptimePct} />

                <div className="grid-2">
                    <div className="panel chart-card">
                        <div className="chart-head">
                            <div className="eyebrow">7-day throughput - reps</div>
                            <span className="badge red">
                                <span className="inline-block h-[7px] w-[7px] rounded-full bg-[var(--red)]" /> Live
                            </span>
                        </div>
                        <div className="chart-wrap">
                            <ThroughputChart data={stats.series.map((d) => ({ d: d.date.slice(5), v: d.reps }))} />
                        </div>
                    </div>

                    <div className="panel backlog">
                        <div className="chart-head mb-1">
                            <div className="eyebrow">Today's tasks</div>
                            <span className="mono text-[11px] text-[var(--tx-4)]">{tasks.length} open</span>
                        </div>
                        {tasks.map((t, idx) => (
                            <button
                                key={t.task_id}
                                type="button"
                                className="ticket w-full text-left"
                                onClick={() => navigate(`/workout/${t.exercise_id}`)}
                                data-testid={`task-${t.exercise_slug}`}
                            >
                                <span className="tk-id">#{String(idx + 1).padStart(2, "0")}</span>
                                <div>
                                    <div className="tk-name">{t.exercise_name.toUpperCase()}</div>
                                    <div className="tk-prog">{t.completed_reps}/{t.target_reps} reps</div>
                                </div>
                                <div className="tk-right">
                                    <span className={`badge ${idx === 0 ? "red" : idx === 1 ? "amber" : ""}`}>{idx === 0 ? "P1" : idx === 1 ? "P2" : "P3"}</span>
                                    {t.completed ? <Trophy size={16} className="text-[var(--green)]" /> : <ChevronRight size={16} />}
                                </div>
                            </button>
                        ))}
                        {tasks.length === 0 && <div className="text-sm text-[var(--tx-3)] py-4">No tasks today.</div>}
                    </div>
                </div>
            </div>

            <MobileDashboard
                stats={stats}
                streak={streak}
                tasks={tasks}
                workouts={workouts}
                heat={heat}
                uptimePct={uptimePct}
                firstName={firstName}
                squatsId={squatsId}
                navigate={navigate}
            />
        </div>
    );
}

function MobileDashboard({ stats, streak, tasks, workouts, heat, uptimePct, firstName, squatsId, navigate }) {
    const user = stats.user;
    const [info, setInfo] = useState(null); // tap-to-explain sheet content
    const isNewUser = (stats.total_workouts || 0) === 0;
    const usual = mostFrequentExercise(workouts);
    const openTask = tasks.find((task) => !task.completed) || tasks[0];
    const now = new Date();
    const todayLabel = `// ${now.toLocaleDateString(undefined, { weekday: "long" }).toLowerCase()} - ${now.getDate()} ${now.toLocaleDateString(undefined, { month: "short" }).toLowerCase()}`;
    const week = buildWeekDots(stats.series, { createdAt: user.created_at, hasHistory: !isNewUser });
    const totalReps = stats.series.reduce((sum, item) => sum + (Number(item.reps) || 0), 0);
    const metToday = Boolean(streak?.met_today);
    const quota = streak?.quota;
    const progress = streak?.progress;
    const routineItems = streak?.mode === "routine" ? (streak?.routine?.items || []) : [];
    const quotaText = routineItems.length
        ? `Routine: ${routineItems.filter((i) => i.completed).length}/${routineItems.length} moves done — finish all to lock your streak.`
        : quota
        ? `${Math.round(progress?.coins_total || 0)}/${quota.required_coins} coins - ${progress?.exercises_total || 0}/${quota.required_exercises} workout${quota.required_exercises === 1 ? "" : "s"}`
        : "One set, about 90 seconds, streak survives.";

    return (
        <div className="mobile-only">
            <div className="m-greet">{todayLabel}</div>
            <h1 className="m-h">Morning, {titleCase(firstName)}.</h1>

            <div className="streak-hero">
                <StreakRing
                    days={user.streak_count}
                    pct={Math.min(92, Math.max(18, user.streak_count * 12.5))}
                    sub={isNewUser ? "your journey starts here" : `${uptimePct}% uptime`}
                />
                <div className="lvl">
                    <span className="badge gold"><Award size={13} />{tierBadge(streak, user.streak_count)}</span>
                </div>
                <div className="next">{nextTierText(streak, user.streak_count)}</div>
                <div className="week-dots">
                    {week.map((w, i) => (
                        <div className="wd" key={`${w.d}-${i}`}>
                            <i className={`${w.s === 1 ? "ok" : w.s === 2 ? "warn" : w.s === 3 ? "idle" : "down"} ${w.today ? "now" : ""}`} />
                            <span>{w.d}</span>
                        </div>
                    ))}
                </div>
            </div>

            {isNewUser ? (
                <div className="today safe" data-testid="day-zero-card">
                    <div className="row1">
                        <span className="tt"><Sparkles size={14} />Day 1 starts now</span>
                    </div>
                    <div className="big">Do your first workout</div>
                    <div className="desc">One set, about 90 seconds. Your camera counts the reps — no equipment needed.</div>
                    <button className="btn-block" onClick={() => navigate(squatsId ? `/workout/${squatsId}` : "/exercises")} data-testid="day-zero-start">
                        <Play size={18} /> Start with Squats
                    </button>
                </div>
            ) : (                <div className={metToday ? "today safe" : "today"}>
                    <div className="row1">
                        <span className="tt">{metToday ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}{metToday ? "Streak safe" : user.streak_count > 0 ? "Streak at risk" : "Start a new streak"}</span>
                        <span className="timer">{endOfDayLabel(now, streak?.day_ends_in_seconds)}</span>
                    </div>
                    <div className="big">{metToday ? "Daily goal done" : "Daily goal still open"}</div>
                    <div className="desc">{metToday ? "Streak secured for today. Extra workouts still earn coins." : quotaText}</div>
                    {!routineItems.length && streak?.next_quota && quota && (
                        <div className="qn-mobile" data-testid="quota-next-preview-mobile">
                            Daily goal {quota.required_coins} coins — rises to {streak.next_quota.required_coins}/day at streak day {streak.next_quota.at_streak_day}
                        </div>
                    )}
                    <button className="btn-block" onClick={() => navigate(usual ? `/workout/${usual.exercise_id}` : openTask ? `/workout/${openTask.exercise_id}` : "/exercises")}>
                        <Play size={18} /> {usual ? `Start now - ${titleCase(usual.exercise_name)}` : "Start now"}
                    </button>
                </div>
            )}

            {isNewUser && <NewUserBoardTeaser />}

            <GettingStartedCard totalWorkouts={stats.total_workouts || 0} squatsId={squatsId} />

            <DailyChallengeCard variant="strip" />

            <div className="qstats">
                <button type="button" className="qstat gold" onClick={() => setInfo(STAT_INFO.coins)} data-testid="qstat-coins"><div className="k"><Coins size={12} />Coins</div><div className="v">{user.coins.toFixed(0)}</div></button>
                <button type="button" className="qstat" onClick={() => setInfo(STAT_INFO.fit)} data-testid="qstat-fit"><div className="k"><Target size={12} />Fit score</div><div className="v">{user.fit_score.toFixed(0)}</div></button>
                <button type="button" className="qstat" onClick={() => setInfo(STAT_INFO.workouts)} data-testid="qstat-workouts"><div className="k"><GitCommitHorizontal size={12} />Workouts</div><div className="v">{stats.week_workouts}</div></button>
            </div>

            <div className="m-sec">
                <span className="lab">{routineItems.length ? "My routine" : "Today's moves"}</span>
                <button className="more" onClick={() => navigate(routineItems.length ? "/goals" : "/exercises")} data-testid="dashboard-moves-more">
                    {routineItems.length ? "Edit goals" : "Library"} <ChevronRight size={13} />
                </button>
            </div>
            {usual && !isNewUser && !routineItems.length && (
                <MobileMoveRow
                    title={usual.exercise_name}
                    meta="Your usual - tap to repeat"
                    badge="GO"
                    onClick={() => navigate(`/workout/${usual.exercise_id}`)}
                />
            )}
            {routineItems.length
                ? routineItems.map((it) => (
                    <MobileMoveRow
                        key={it.exercise_id}
                        title={it.name}
                        meta={`${it.done}/${it.target}${it.unit === "seconds" ? "s" : " reps"} today`}
                        badge={it.completed ? "DONE" : "GOAL"}
                        onClick={() => navigate(`/workout/${it.exercise_id}`)}
                    />
                ))
                : tasks.map((task, idx) => (
                    <MobileMoveRow
                        key={task.task_id}
                        title={task.exercise_name}
                        meta={`Target ${task.target_reps} reps`}
                        badge={idx === 0 ? "P1" : idx === 1 ? "P2" : "P3"}
                        onClick={() => navigate(`/workout/${task.exercise_id}`)}
                    />
                ))}

            <div className="m-sec"><span className="lab">This week - reps</span></div>
            <div className="mini-chart">
                <div className="top">
                    <div className="tot">{totalReps}<small>reps</small></div>
                    <span className="badge cyan">live</span>
                </div>
                <div style={{ height: 130 }}>
                    <ThroughputChart data={stats.series.map((d) => ({ d: d.date.slice(5), v: d.reps }))} height={150} />
                </div>
            </div>

            <div className="m-sec">
                <span className="lab">Recent workouts</span>
                <button className="more" onClick={() => navigate("/history")}>All <ChevronRight size={13} /></button>
            </div>
            <div className="mclog">
                {workouts.slice(0, 3).map((workout) => (
                    <MobileCommit key={workout.workout_id} workout={workout} />
                ))}
                {workouts.length === 0 && (
                    <div className="mcommit">
                        <span className="cdot prog" />
                        <div className="cmid"><div className="cex">NO WORKOUTS YET</div><div className="cms">Your first session will show up here.</div></div>
                        <div className="cr"><div className="a">+0</div><div className="b">0 reps</div></div>
                    </div>
                )}
            </div>

            <StatInfoSheet info={info} onClose={() => setInfo(null)} />
        </div>
    );
}

function StatTile({ icon, kind = "", label, value, unit, sub, testid }) {
    return (
        <div className={`stat-tile ${kind}`} data-testid={testid}>
            <div className="lbl">
                <span className="eyebrow">{label}</span>
                <span className="ic">{icon}</span>
            </div>
            <div className="val">
                {value}
                {unit && <small>{unit}</small>}
            </div>
            <div className="sub">{sub}</div>
        </div>
    );
}

function ThroughputChart({ data, height = 230 }) {
    const width = 760;
    const pad = { l: 38, r: 14, t: 14, b: 26 };
    const values = data.length ? data.map((d) => Number(d.v) || 0) : [0];
    const max = Math.max(...values, 10) * 1.15;
    const iw = width - pad.l - pad.r;
    const ih = height - pad.t - pad.b;
    const x = (i) => pad.l + (iw * i) / Math.max(1, data.length - 1);
    const y = (v) => pad.t + ih - (ih * v) / max;
    const points = data.map((d, i) => [x(i), y(Number(d.v) || 0)]);
    const line = smoothPath(points);
    const baseY = pad.t + ih;
    const area = points.length ? `${line} L ${points[points.length - 1][0]} ${baseY} L ${points[0][0]} ${baseY} Z` : "";
    const ticks = [0, max / 4, max / 2, (max * 3) / 4, max].map((v) => Math.round(v));

    return (
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
            <defs>
                <linearGradient id="throughput-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--cyan)" stopOpacity="0.28" />
                    <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0" />
                </linearGradient>
            </defs>
            {ticks.map((tick, i) => {
                const yy = y(tick);
                return (
                    <g key={i}>
                        <line x1={pad.l} y1={yy} x2={width - pad.r} y2={yy} stroke="var(--bdr-soft)" strokeWidth="1" />
                        <text x={pad.l - 8} y={yy + 3} textAnchor="end" fontFamily="var(--font-mono)" fontSize="10" fill="var(--tx-4)">{tick}</text>
                    </g>
                );
            })}
            {area && <path d={area} fill="url(#throughput-fill)" />}
            {line && <path d={line} fill="none" stroke="var(--cyan)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />}
            {points.map((p, i) => (
                <g key={i}>
                    {i === points.length - 1 && <circle cx={p[0]} cy={p[1]} r="6" fill="var(--cyan)" opacity="0.18" />}
                    <circle cx={p[0]} cy={p[1]} r={i === points.length - 1 ? 3.5 : 2.5} fill={i === points.length - 1 ? "var(--cyan)" : "var(--bg)"} stroke="var(--cyan)" strokeWidth="1.6" />
                    <text x={p[0]} y={height - 8} textAnchor="middle" fontFamily="var(--font-mono)" fontSize="9.5" fill="var(--tx-4)">{data[i].d}</text>
                </g>
            ))}
        </svg>
    );
}

function StreakRing({ days = 0, pct = 75, sub }) {
    const radius = 86;
    const circumference = 2 * Math.PI * radius;
    const dash = (pct / 100) * circumference;

    return (
        <div className="ringwrap">
            <svg width="188" height="188" viewBox="0 0 188 188" aria-hidden="true">
                <circle cx="94" cy="94" r={radius} fill="none" stroke="var(--elev)" strokeWidth="12" />
                <circle
                    cx="94"
                    cy="94"
                    r={radius}
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${circumference}`}
                    transform="rotate(-90 94 94)"
                    style={{ filter: "drop-shadow(0 0 8px var(--accent-glow))" }}
                />
            </svg>
            <div className="center">
                <div className="num">{days}<small>d</small></div>
                <div className="upt">{sub}</div>
            </div>
        </div>
    );
}

// New users have zero history — show this week's top movers so the board feels
// reachable and the first session has a "get on the board" motive.
function NewUserBoardTeaser() {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    useEffect(() => {
        leaderboard("week")
            .then((r) => setRows((r.data || []).slice(0, 3)))
            .catch((err) => { console.warn("[dashboard] board teaser failed:", err?.message || err); setRows([]); });
    }, []);
    if (!rows || rows.length === 0) return null;
    const medal = ["var(--gold)", "#c8cdd8", "#c98f5e"];
    return (
        <>
            <div className="m-sec">
                <span className="lab">This week's leaders</span>
                <button className="more" onClick={() => navigate("/leaderboard")} data-testid="newuser-board-more">
                    See all <ChevronRight size={13} />
                </button>
            </div>
            <div className="setlist" data-testid="newuser-leaderboard">
                {rows.map((r, i) => (
                    <div className="setrow" key={r.user_id} data-testid={`newuser-board-row-${i + 1}`}>
                        <span className="sic" style={{ color: medal[i] }}><Trophy /></span>
                        <span className="sl">
                            {(r.name || "Someone").split(" ")[0]}
                            <div className="text-[11px] text-[var(--tx-4)]">{r.workouts || 0} workout{(r.workouts || 0) === 1 ? "" : "s"} this week</div>
                        </span>
                        <span className="sr" style={{ color: i === 0 ? "var(--gold)" : undefined }}>{Math.round(r.fit_score || 0)} pts</span>
                    </div>
                ))}
                <div className="setrow" data-testid="newuser-board-you">
                    <span className="sic" style={{ color: "var(--accent)" }}><Sparkles /></span>
                    <span className="sl" style={{ color: "var(--accent)" }}>You</span>
                    <span className="sr">one session puts you here</span>
                </div>
            </div>
        </>
    );
}

function MobileMoveRow({ title, meta, badge, onClick }) {
    return (
        <button className="move" onClick={onClick} type="button">
            <div className="thumb"><Dumbbell /></div>
            <div className="mid">
                <div className="mn">{title.toUpperCase()}</div>
                <div className="mmeta"><span>{meta}</span>{badge && <span className={`badge ${badge === "P1" ? "red" : badge === "P2" ? "amber" : badge === "GO" || badge === "DONE" ? "green" : badge === "GOAL" ? "cyan" : ""}`} style={{ padding: "2px 6px", fontSize: 9 }}>{badge}</span>}</div>
            </div>
            <span className="play"><Play /></span>
        </button>
    );
}

function MobileCommit({ workout }) {
    return (
        <div className="mcommit">
            <span className={`cdot ${workout.status === "completed" ? "" : "prog"}`} />
            <div className="cmid">
                <div className="cex">{workout.exercise_name?.toUpperCase()}</div>
                <div className="cms">{workout.status} - {Math.round(workout.duration_sec || 0)}s</div>
            </div>
            <div className="cr">
                <div className="a">+{Number(workout.coins_earned || 0).toFixed(1)}</div>
                <div className="b">{workout.reps || 0} reps</div>
            </div>
        </div>
    );
}

// First-ever visit (no cached data yet): layout-shaped skeleton, no spinner.
function DashboardSkeleton() {
    return (
        <div className="skelwrap" data-testid="dashboard-skeleton">
            <div className="skel" style={{ width: 140, height: 12 }} />
            <div className="skel" style={{ width: 230, height: 30 }} />
            <div className="skel skel-ring" />
            <div className="skel" style={{ height: 170, borderRadius: 16 }} />
            <div style={{ display: "flex", gap: 10 }}>
                <div className="skel" style={{ flex: 1, height: 74, borderRadius: 14 }} />
                <div className="skel" style={{ flex: 1, height: 74, borderRadius: 14 }} />
                <div className="skel" style={{ flex: 1, height: 74, borderRadius: 14 }} />
            </div>
            <div className="skel" style={{ height: 60, borderRadius: 14 }} />
            <div className="skel" style={{ height: 60, borderRadius: 14 }} />
        </div>
    );
}

function titleCase(value) {
    const lower = String(value || "Athlete").toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// The exercise the user repeats the most among recent completed workouts —
// real users tend to have one go-to move they look for every day.
function mostFrequentExercise(workouts = []) {
    const counts = new Map();
    for (const w of workouts) {
        if (w.status !== "completed" || !w.exercise_id) continue;
        const entry = counts.get(w.exercise_id) || { exercise_id: w.exercise_id, exercise_name: w.exercise_name, n: 0 };
        entry.n += 1;
        counts.set(w.exercise_id, entry);
    }
    let best = null;
    for (const entry of counts.values()) {
        if (!best || entry.n > best.n) best = entry;
    }
    return best;
}

function smoothPath(points) {
    if (!points.length) return "";
    if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;
    let d = `M ${points[0][0]} ${points[0][1]}`;
    for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i - 1] || points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2] || p2;
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = p1[1] + (p2[1] - p0[1]) / 6;
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
    }
    return d;
}

function makeHeat(streakCount) {
    return Array.from({ length: 30 }, (_, i) => {
        const liveStart = Math.max(0, 30 - Math.max(1, streakCount));
        if (i >= liveStart) return 1;
        if (i === liveStart - 1) return 2;
        if (i === 3 || i === 17) return 0;
        if (i === 11) return 2;
        return 1;
    });
}
